import { boot, eraroCodesMap, isEraroError } from '@dazn/bootstrap-lib';

import { LifecycleTriggers } from '../lifecycle/lifecycle';

import { clean } from './clean';
import { createFetchChapterError } from './errors';
import { fetchChapterIndex } from './fetchChapterIndex';
import { insert } from './insert';
import { parse } from './parse';
import { transform } from './transform';

type Win = typeof window;
export const loadChapter = ({
    window,
    url,
    baseUrl,
    chapterName,
    chapterCountry,
    triggers,
    garbageCollector,
    selectorsExcludedFromCleaning = [],
}: {
    window: Win;
    url: string;
    baseUrl: string;
    chapterName: boot.config.ChapterName;
    chapterCountry: boot.config.AnyCountry;
    triggers: LifecycleTriggers;
    garbageCollector: () => void;
    selectorsExcludedFromCleaning?: string[];
}) => {
    const { document } = window;
    const { willLoadEvents, willUnloadEvents, didUnloadEvents } = triggers;

    let chapterVersion: string | null = null;

    type Trigger = typeof willLoadEvents;
    const triggerEvents =
        (trigger: Trigger) =>
        <T>(value: T) =>
            trigger({
                chapter: chapterName,
                country: chapterCountry,
            }).then(() => value);

    return triggerEvents(willLoadEvents)(null)
        .then(() => fetchChapterIndex(window.fetch, url))
        .then(
            result => {
                if (result.chapter && chapterName !== result.chapter) {
                    throw createFetchChapterError(
                        `Expected ${chapterName} but got ${result.chapter} instead`,
                        eraroCodesMap.internal.NotFound,
                    );
                }
                chapterVersion = result.version;
                return result;
            },
            (error: Error) => {
                if (isEraroError(error)) {
                    throw error;
                }

                throw createFetchChapterError(
                    'Unexpected error',
                    eraroCodesMap.internal.GenericError,
                    error,
                );
            },
        )
        .then(
            fetchResponse => {
                const existingBase = document.querySelector('base');
                if (existingBase !== null) {
                    existingBase.setAttribute('href', baseUrl);
                } else {
                    const base = document.createElement('base');
                    base.setAttribute('href', baseUrl);
                    document.head.appendChild(base);
                }
                return fetchResponse;
            },
            (error: Error) => {
                if (isEraroError(error)) {
                    throw error;
                }
                throw createFetchChapterError(
                    'Chapter not found',
                    eraroCodesMap.internal.NotFound,
                    error,
                );
            },
        )
        .then(parse(window.DOMParser))
        .then(transform, (error: Error) => {
            if (isEraroError(error)) {
                throw error;
            }
            throw createFetchChapterError(
                'Failed to parse document',
                eraroCodesMap.internal.ParserError,
                error,
            );
        })
        .then(triggerEvents(willUnloadEvents))
        .then(transformedNodes => {
            garbageCollector();
            clean(document, selectorsExcludedFromCleaning);
            return triggerEvents(didUnloadEvents)(transformedNodes);
        })
        .then(transformedNodes => {
            insert(document, transformedNodes);
            return chapterVersion;
        });
};
